import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, differenceInDays } from 'date-fns';
import {
    reportService,
    IReportPreviewResponse,
    IReportRequest,
    PM_REPORT_FREQUENCY_OPTIONS,
    type PMReportFrequencyFilter,
    type PMReportExcludableStatus
} from '../../services/reportService';
import { ResolvedImage } from '../../components/media/ResolvedImage';
import PageBreadcrumb from '../../shared/PageBreadCrumb';
import ImageViewerModal from './components/ImageViewerModal';
import {
    Calendar,
    FileText,
    ArrowLeft,
    TrendingUp,
    CheckCircle,
    XCircle,
    AlertCircle,
    Clock,
    Download,
    Eye,
    ChevronLeft,
    ChevronRight,
    Search,
    SlidersHorizontal,
    X as XIcon,
    AlertTriangle,
    Timer
} from 'lucide-react';

interface LocationState {
    selectedProjectIds: string[];
    startDate: string;
    endDate: string;
    frequency?: PMReportFrequencyFilter;
    excludeStatuses?: PMReportExcludableStatus[];
    projects: any[];
}

interface ImageThumbCellProps {
    urls?: string[];
    title: string;
    onClick: () => void;
}

const ImageThumbCell: React.FC<ImageThumbCellProps> = ({ urls, title, onClick }) => {
    if (!urls || urls.length === 0) return <span className="text-gray-400 text-xs">-</span>;

    const src = urls[0];
    const hasThumbSource = typeof src === 'string' && src.trim().length > 0;

    return (
        <button
            type="button"
            onClick={onClick}
            className="group relative inline-flex items-center justify-center transition-transform hover:scale-105"
            title={title}
        >
            {hasThumbSource ? (
                <ResolvedImage
                    mediaSrc={src}
                    alt={title}
                    className="h-8 w-8 rounded-md object-cover ring-1 ring-gray-200 transition-all group-hover:ring-blue-500 dark:ring-gray-700"
                    loading="lazy"
                />
            ) : (
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-[10px] font-semibold text-gray-600 ring-1 ring-gray-200 transition-all group-hover:ring-blue-500 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700">
                    View
                </span>
            )}
            {urls.length > 1 && (
                <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-0.5 text-[9px] font-bold text-white">
                    {urls.length}
                </span>
            )}
        </button>
    );
};

const PreviewReport: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as LocationState;

    const [previewData, setPreviewData] = useState<IReportPreviewResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
    const [previewPage, setPreviewPage] = useState(1);
    const [previewLimit, setPreviewLimit] = useState(20);
    const [generating, setGenerating] = useState(false);

    // Image viewing state
    const [viewingImages, setViewingImages] = useState<{ urls: string[]; title: string } | null>(null);

    /**
     * Local preview filters — narrow the visible task list without re-running the report.
     * Server-side `excludeStatuses` (carried in `state`) already removed unwanted statuses;
     * these toggles let users drill into what's left.
     */
    const [filterSearch, setFilterSearch] = useState('');
    const [filterStatuses, setFilterStatuses] = useState<Set<string>>(new Set());
    const [filterOutcomes, setFilterOutcomes] = useState<Set<string>>(new Set());
    const [filterDelayOnly, setFilterDelayOnly] = useState(false);
    const [filterIssueOnly, setFilterIssueOnly] = useState(false);
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);

    const toggleSetValue = (set: Set<string>, value: string): Set<string> => {
        const next = new Set(set);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        return next;
    };

    const clearAllFilters = () => {
        setFilterSearch('');
        setFilterStatuses(new Set());
        setFilterOutcomes(new Set());
        setFilterDelayOnly(false);
        setFilterIssueOnly(false);
    };

    const filterCount =
        (filterSearch.trim() ? 1 : 0) +
        filterStatuses.size +
        filterOutcomes.size +
        (filterDelayOnly ? 1 : 0) +
        (filterIssueOnly ? 1 : 0);

    const effectiveFrequency = state?.frequency ?? 'all';
    const effectiveExcludeStatuses = React.useMemo(
        () => state?.excludeStatuses ?? [],
        [state?.excludeStatuses]
    );

    const loadPreview = React.useCallback(async () => {
        if (!state) return;

        try {
            setLoading(true);
            setError(null);
            const projectId = state.selectedProjectIds[currentProjectIndex];
            const request: IReportRequest = {
                projectId,
                startDate: new Date(state.startDate).toISOString(),
                endDate: new Date(state.endDate).toISOString(),
                ...(effectiveFrequency !== 'all' ? { frequency: effectiveFrequency } : {}),
                ...(effectiveExcludeStatuses.length > 0 ? { excludeStatuses: effectiveExcludeStatuses } : {})
            };
            const preview = await reportService.getPreventiveMaintenancePreview(request);
            setPreviewData(preview);
            setPreviewPage(1);
        } catch (err: any) {
            console.error('Preview generation failed:', err);
            setError(err.message || 'Failed to load preview');
        } finally {
            setLoading(false);
        }
    }, [state, currentProjectIndex, effectiveFrequency, effectiveExcludeStatuses]);

    useEffect(() => {
        if (!state || !state.selectedProjectIds || state.selectedProjectIds.length === 0) {
            navigate('/reports/preventive-maintenance');
            return;
        }
        void loadPreview();
    }, [state, navigate, loadPreview]);

    const handleGenerate = async () => {
        if (!state) return;

        try {
            setGenerating(true);
            for (let i = 0; i < state.selectedProjectIds.length; i++) {
                const projectId = state.selectedProjectIds[i];
                const project = state.projects.find(p => p.id === projectId);
                const request: IReportRequest = {
                    projectId,
                    startDate: new Date(state.startDate).toISOString(),
                    endDate: new Date(state.endDate).toISOString(),
                    ...(effectiveFrequency !== 'all' ? { frequency: effectiveFrequency } : {}),
                    ...(effectiveExcludeStatuses.length > 0 ? { excludeStatuses: effectiveExcludeStatuses } : {})
                };
                const result = await reportService.generatePreventiveMaintenanceReport(request);
                const projectName = project?.projectName || project?.projectId || 'Unknown';
                reportService.downloadExcelFile(result.blob, projectName, state.startDate, state.endDate);
            }
            alert(`Successfully generated ${state.selectedProjectIds.length} report(s)!`);
        } catch (err: any) {
            console.error('Report generation failed:', err);
            alert(err.message || 'Failed to generate reports');
        } finally {
            setGenerating(false);
        }
    };

    const handleGeneratePdf = async () => {
        if (!state) return;

        try {
            setGenerating(true);
            for (let i = 0; i < state.selectedProjectIds.length; i++) {
                const projectId = state.selectedProjectIds[i];
                const project = state.projects.find(p => p.id === projectId);
                const request: IReportRequest = {
                    projectId,
                    startDate: new Date(state.startDate).toISOString(),
                    endDate: new Date(state.endDate).toISOString(),
                    ...(effectiveFrequency !== 'all' ? { frequency: effectiveFrequency } : {}),
                    ...(effectiveExcludeStatuses.length > 0 ? { excludeStatuses: effectiveExcludeStatuses } : {})
                };
                const result = await reportService.generatePreventiveMaintenanceReportPdf(request);
                const projectName = project?.projectName || project?.projectId || 'Unknown';
                reportService.downloadPdfFile(result.blob, projectName, state.startDate, state.endDate);
            }
            alert(`Successfully generated ${state.selectedProjectIds.length} PDF report(s)!`);
        } catch (err: any) {
            console.error('PDF report generation failed:', err);
            alert(err.message || 'Failed to generate PDF reports');
        } finally {
            setGenerating(false);
        }
    };

    const handlePreviousProject = () => {
        if (currentProjectIndex > 0) {
            setCurrentProjectIndex(currentProjectIndex - 1);
        }
    };

    const handleNextProject = () => {
        if (currentProjectIndex < state.selectedProjectIds.length - 1) {
            setCurrentProjectIndex(currentProjectIndex + 1);
        }
    };

    const handleViewImages = (urls: string[], title: string) => {
        if (!urls || urls.length === 0) return;
        const normalized = urls.filter(Boolean).map(String);
        if (normalized.length === 0) return;
        setViewingImages({ urls: normalized, title });
    };

    /** Tasks after applying local filters (search + status + outcome + delay/issue toggles). */
    const filteredTasks = React.useMemo(() => {
        if (!previewData || !previewData.tasks) return [];
        const q = filterSearch.trim().toLowerCase();
        return previewData.tasks.filter((t: any) => {
            if (q) {
                const blob = [
                    t.equipmentName,
                    t.checkpointName,
                    t.specification,
                    t.assignedTo,
                    t.remarks,
                    t.issueDescription,
                    t.actionTaken,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                if (!blob.includes(q)) return false;
            }
            if (filterStatuses.size > 0 && !filterStatuses.has(String(t.status || ''))) return false;
            if (filterOutcomes.size > 0 && !filterOutcomes.has(String(t.outcome || ''))) return false;
            if (filterDelayOnly && !((t.delay ?? 0) > 0)) return false;
            if (filterIssueOnly && !(t.issueDescription || (t.issuePriority && t.issuePriority !== ''))) return false;
            return true;
        });
    }, [previewData, filterSearch, filterStatuses, filterOutcomes, filterDelayOnly, filterIssueOnly]);

    /** Distinct status / outcome values present in the loaded preview (for chip pickers). */
    const availableStatuses = React.useMemo(() => {
        const s = new Set<string>();
        for (const t of previewData?.tasks ?? []) if (t?.status) s.add(String(t.status));
        return [...s];
    }, [previewData]);
    const availableOutcomes = React.useMemo(() => {
        const s = new Set<string>();
        for (const t of previewData?.tasks ?? []) if (t?.outcome) s.add(String(t.outcome));
        return [...s];
    }, [previewData]);

    // Reset to page 1 whenever filters narrow the list — avoids landing on an empty page.
    React.useEffect(() => {
        setPreviewPage(1);
    }, [filterSearch, filterStatuses, filterOutcomes, filterDelayOnly, filterIssueOnly]);

    const paginatedTasks = React.useMemo(() => {
        const start = (previewPage - 1) * previewLimit;
        return filteredTasks.slice(start, start + previewLimit);
    }, [filteredTasks, previewPage, previewLimit]);

    const totalPages = filteredTasks.length > 0 ? Math.ceil(filteredTasks.length / previewLimit) : 0;

    /**
     * Show the Issue & Action column group only when at least one task in the
     * current preview carries issue data. Keeps the table compact for clean runs
     * and mirrors the Excel/PDF output, which drop those columns identically.
     */
    const showIssueColumns = React.useMemo(() => {
        if (!previewData?.tasks) return false;
        return previewData.tasks.some((t: any) => {
            const priority = t.priority ?? t.issuePriority;
            return (
                (t.issueDescription && String(t.issueDescription).trim().length > 0) ||
                (t.actionTaken && String(t.actionTaken).trim().length > 0) ||
                (priority && String(priority).trim().length > 0) ||
                ((t.issueDelay ?? 0) > 0) ||
                (Array.isArray(t.issueBeforeImages) && t.issueBeforeImages.length > 0) ||
                (Array.isArray(t.issueAfterImages) && t.issueAfterImages.length > 0)
            );
        });
    }, [previewData]);

    const tableColumnCount = showIssueColumns ? 23 : 14;

    const currentProject = state?.projects.find(p => p.id === state.selectedProjectIds[currentProjectIndex]);

    if (!state) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/20 to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
            <PageBreadcrumb pageTitle="Report Preview" />

            {/* Header: white with thin left green gradient strip (same as Preventive Maintenance Report) */}
            <header className="sticky top-0 z-30 border-b border-slate-200/60 bg-white/95 backdrop-blur-xl shadow-sm dark:border-slate-700 dark:bg-slate-900/95">
                <div
                    className="absolute left-0 top-0 bottom-0 w-2 shrink-0"
                    style={{
                        background: 'linear-gradient(to right, #92D050 0%, #059669 45%, rgba(255,255,255,0.95) 100%)',
                    }}
                    aria-hidden
                />
                <div className="relative mx-auto max-w-[1920px] px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                        {/* Left: Back Button & Title */}
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/reports/preventive-maintenance')}
                                className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-bold text-slate-700 transition-all hover:bg-slate-200 hover:shadow-md active:scale-95 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back
                            </button>
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 items-center justify-center rounded-xl bg-white px-3 ring-1 ring-slate-200 shadow-sm dark:bg-slate-800 dark:ring-slate-700">
                                    <img
                                        src="/images/logo/logo.svg"
                                        alt="Sorigin"
                                        className="h-7 w-auto"
                                        draggable={false}
                                    />
                                </div>
                                <div className="hidden h-10 w-px bg-slate-200 dark:bg-slate-700 sm:block" aria-hidden />
                                <div>
                                    <h1 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
                                        Preventive Maintenance Report
                                    </h1>
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                        {currentProject?.projectName || 'Loading...'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Center: Project Navigation */}
                        {state.selectedProjectIds.length > 1 && (
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handlePreviousProject}
                                    disabled={currentProjectIndex === 0}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border-2 border-slate-200 bg-white transition-all hover:border-green-500 hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:bg-slate-800 dark:hover:border-green-500 dark:hover:bg-green-900/30"
                                >
                                    <ChevronLeft className="h-5 w-5" />
                                </button>
                                <div className="rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-2 dark:from-green-900/20 dark:to-emerald-900/20">
                                    <p className="text-xs font-medium text-slate-600 dark:text-slate-400">Project</p>
                                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                                        {currentProjectIndex + 1} of {state.selectedProjectIds.length}
                                    </p>
                                </div>
                                <button
                                    onClick={handleNextProject}
                                    disabled={currentProjectIndex === state.selectedProjectIds.length - 1}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border-2 border-slate-200 bg-white transition-all hover:border-green-500 hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-700 dark:bg-slate-800 dark:hover:border-green-500 dark:hover:bg-green-900/30"
                                >
                                    <ChevronRight className="h-5 w-5" />
                                </button>
                            </div>
                        )}

                        {/* Right: Generate Excel / PDF */}
                        <div className="flex flex-wrap items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={handleGeneratePdf}
                                disabled={generating}
                                className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-400 active:scale-95 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                            >
                                {generating ? (
                                    <>
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-400 border-t-slate-700" />
                                        <span>Working…</span>
                                    </>
                                ) : (
                                    <>
                                        <FileText className="h-4 w-4 text-red-600" />
                                        <span>Generate PDF</span>
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={handleGenerate}
                                disabled={generating}
                                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-500/40 transition-all hover:scale-105 hover:shadow-xl hover:shadow-green-500/50 active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-green-700 to-emerald-700 opacity-0 transition-opacity group-hover:opacity-100"></div>
                                <div className="relative flex items-center gap-2">
                                    {generating ? (
                                        <>
                                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                                            <span>Generating…</span>
                                        </>
                                    ) : (
                                        <>
                                            <Download className="h-4 w-4" />
                                            <span>Generate Excel</span>
                                        </>
                                    )}
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Content - compact top/bottom to maximize table space */}
            <div className="mx-auto max-w-[1920px] px-4 py-4">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-24">
                        <div className="relative">
                            <div className="h-16 w-16 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Eye className="h-6 w-6 text-blue-600 animate-pulse" />
                            </div>
                        </div>
                        <p className="mt-6 text-base font-bold text-gray-900 dark:text-white">Loading Preview</p>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Please wait...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center py-24">
                        <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20">
                            <XCircle className="h-10 w-10 text-red-600" />
                        </div>
                        <p className="mt-4 text-lg font-bold text-gray-900 dark:text-white">Error Loading Preview</p>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{error}</p>
                    </div>
                ) : previewData ? (
                    <div className="space-y-3">
                        {/* Compact Info + Project + Summary strip */}
                        <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/80 shadow-sm overflow-hidden">
                            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5 text-xs">
                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                    <Calendar className="h-3.5 w-3.5 text-blue-600" />
                                    <span className="font-medium text-gray-700 dark:text-gray-300">
                                        {format(new Date(state.startDate), 'dd MMM yyyy')} → {format(new Date(state.endDate), 'dd MMM yyyy')}
                                    </span>
                                    <span className="rounded bg-blue-100 px-1.5 py-0.5 font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                        {differenceInDays(new Date(state.endDate), new Date(state.startDate)) + 1}d
                                    </span>
                                    {effectiveFrequency !== 'all' && (
                                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                                            {PM_REPORT_FREQUENCY_OPTIONS.find((o) => o.value === effectiveFrequency)?.label ??
                                                effectiveFrequency}
                                        </span>
                                    )}
                                    {effectiveExcludeStatuses.length > 0 && (
                                        <span
                                            className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                                            title={`Hidden statuses: ${effectiveExcludeStatuses.join(', ')}`}
                                        >
                                            −{effectiveExcludeStatuses.length} status{effectiveExcludeStatuses.length !== 1 ? 'es' : ''}
                                        </span>
                                    )}
                                </div>
                                <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                    <TrendingUp className="h-3.5 w-3.5" />
                                    {previewData.tasks?.length || 0} records
                                </span>
                                {previewData.project && (
                                    <>
                                        <span className="text-gray-400">|</span>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">{previewData.project.projectName || 'N/A'}</span>
                                        <span className="text-gray-500 dark:text-gray-500">{previewData.project.location || ''}</span>
                                        <span className="text-gray-500 dark:text-gray-500">{previewData.project.projectCode || ''}</span>
                                        <span className="text-gray-500 dark:text-gray-500">
                                            {previewData.project.dcCapacity || 'N/A'} / {previewData.project.acCapacity || 'N/A'} kW
                                        </span>
                                        <span className="text-gray-500 dark:text-gray-500">{previewData.project.maintenanceIncharge || ''}</span>
                                        <span className="text-gray-500 dark:text-gray-500">{previewData.project.siteIncharge || ''}</span>
                                    </>
                                )}
                                {previewData.summary && (
                                    <>
                                        <span className="text-gray-400">|</span>
                                        <span className="rounded bg-blue-500/90 px-1.5 py-0.5 font-semibold text-white">Total {previewData.summary.totalTasks || 0}</span>
                                        <span className="rounded bg-green-500/90 px-1.5 py-0.5 font-semibold text-white">Pass {previewData.summary.passCount || 0}</span>
                                        <span className="rounded bg-red-500/90 px-1.5 py-0.5 font-semibold text-white">Fail {(previewData.summary.failCount || 0) + (previewData.summary.issuesRaised || 0)}</span>
                                        <span className="rounded bg-amber-500/90 px-1.5 py-0.5 font-semibold text-white">Pending {previewData.summary.pendingTasks || 0}</span>
                                        <span className="rounded bg-purple-500/90 px-1.5 py-0.5 font-semibold text-white">Delays {previewData.summary.totalDelays || 0}</span>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* 3 blocks: Customer Details, Project Info, Project Involved (matches Excel above table) */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {/* Block 1: Customer Details */}
                            <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/80 shadow-sm overflow-hidden">
                                <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800/90">
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Customer Details</h3>
                                </div>
                                <div className="space-y-2 px-4 py-3 text-xs">
                                    <div>
                                        <span className="font-medium text-gray-500 dark:text-gray-400">Customer ID:</span>
                                        <span className="ml-1 text-gray-800 dark:text-gray-200">{previewData?.project?.projectCode ?? '—'}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-500 dark:text-gray-400">Customer Name:</span>
                                        <span className="ml-1 text-gray-800 dark:text-gray-200">{previewData?.project?.projectName ?? '—'}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-500 dark:text-gray-400">Company Name:</span>
                                        <span className="ml-1 text-gray-800 dark:text-gray-200">{previewData?.project?.projectName || previewData?.project?.location || '—'}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-500 dark:text-gray-400">Location:</span>
                                        <span className="ml-1 text-gray-800 dark:text-gray-200">{previewData?.project?.location ?? '—'}</span>
                                    </div>
                                </div>
                            </div>
                            {/* Block 2: Project Info */}
                            <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/80 shadow-sm overflow-hidden">
                                <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800/90">
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Project Info</h3>
                                </div>
                                <div className="space-y-2 px-4 py-3 text-xs">
                                    <div>
                                        <span className="font-medium text-gray-500 dark:text-gray-400">DC Capacity:</span>
                                        <span className="ml-1 text-gray-800 dark:text-gray-200">{previewData?.project?.dcCapacity ?? '—'}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-500 dark:text-gray-400">AC Capacity:</span>
                                        <span className="ml-1 text-gray-800 dark:text-gray-200">{previewData?.project?.acCapacity ?? '—'}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-500 dark:text-gray-400">Report Period:</span>
                                        <span className="ml-1 text-gray-800 dark:text-gray-200">
                                            {state?.startDate && state?.endDate
                                                ? `${format(new Date(state.startDate), 'dd MMM yyyy')} – ${format(new Date(state.endDate), 'dd MMM yyyy')}`
                                                : '—'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {/* Block 3: Project Involved */}
                            <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/80 shadow-sm overflow-hidden">
                                <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800/90">
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Project Involved</h3>
                                </div>
                                <div className="space-y-2 px-4 py-3 text-xs">
                                    <div>
                                        <span className="font-medium text-gray-500 dark:text-gray-400">Maintenance Incharge:</span>
                                        <span className="ml-1 text-gray-800 dark:text-gray-200">{previewData?.project?.maintenanceIncharge ?? '—'}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-500 dark:text-gray-400">Site Incharge:</span>
                                        <span className="ml-1 text-gray-800 dark:text-gray-200">{previewData?.project?.siteIncharge ?? '—'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Task Details Table - Prime small compact */}
                        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-gray-800">
                            {/* Header + filter bar */}
                            <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100/60 px-4 py-2.5 dark:border-gray-700 dark:from-gray-800/90 dark:to-gray-800/70">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm">
                                            <SlidersHorizontal className="h-3.5 w-3.5 text-white" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Task Details</h3>
                                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-800/50 tabular-nums">
                                            {filterCount > 0
                                                ? `${filteredTasks.length} of ${previewData.tasks?.length || 0}`
                                                : `${previewData.tasks?.length || 0}`}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {/* Search */}
                                        <div className="relative">
                                            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="search"
                                                value={filterSearch}
                                                onChange={(e) => setFilterSearch(e.target.value)}
                                                placeholder="Search tasks…"
                                                className="w-44 sm:w-56 rounded-md border border-gray-200 bg-white py-1.5 pl-7 pr-7 text-xs placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                                            />
                                            {filterSearch && (
                                                <button
                                                    type="button"
                                                    onClick={() => setFilterSearch('')}
                                                    className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                                >
                                                    <XIcon className="h-3 w-3" />
                                                </button>
                                            )}
                                        </div>

                                        {/* Filter toggle */}
                                        <button
                                            type="button"
                                            onClick={() => setFilterPanelOpen(v => !v)}
                                            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-all ${
                                                filterPanelOpen || filterCount > 0
                                                    ? 'border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-600/30 hover:bg-blue-700'
                                                    : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                                            }`}
                                        >
                                            <SlidersHorizontal className="h-3.5 w-3.5" />
                                            <span className="hidden sm:inline">Filters</span>
                                            {filterCount > 0 && (
                                                <span className={`inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold ${
                                                    filterPanelOpen ? 'bg-white/25 text-white' : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {filterCount}
                                                </span>
                                            )}
                                        </button>

                                        {filterCount > 0 && (
                                            <button
                                                type="button"
                                                onClick={clearAllFilters}
                                                className="text-xs font-semibold text-gray-500 hover:text-red-600 transition-colors"
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {filterPanelOpen && (
                                    <div className="mt-2.5 rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
                                        <div className="grid gap-3 lg:grid-cols-3">
                                            {/* Status chips */}
                                            <div>
                                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                    Status
                                                </p>
                                                {availableStatuses.length === 0 ? (
                                                    <p className="text-[11px] italic text-gray-400">No statuses available</p>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1">
                                                        {availableStatuses.map((s) => {
                                                            const active = filterStatuses.has(s);
                                                            return (
                                                                <button
                                                                    key={s}
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setFilterStatuses(prev => toggleSetValue(prev, s))
                                                                    }
                                                                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-all ${
                                                                        active
                                                                            ? 'border-blue-600 bg-blue-600 text-white'
                                                                            : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                                                                    }`}
                                                                >
                                                                    {s === 'completed' ? (
                                                                        <CheckCircle className="h-2.5 w-2.5" />
                                                                    ) : s === 'in_progress' ? (
                                                                        <Clock className="h-2.5 w-2.5" />
                                                                    ) : null}
                                                                    {s}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Outcome chips */}
                                            <div>
                                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                    Outcome
                                                </p>
                                                {availableOutcomes.length === 0 ? (
                                                    <p className="text-[11px] italic text-gray-400">No outcomes available</p>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1">
                                                        {availableOutcomes.map((o) => {
                                                            const active = filterOutcomes.has(o);
                                                            const tone =
                                                                o === 'Pass'
                                                                    ? active
                                                                        ? 'border-emerald-600 bg-emerald-600 text-white'
                                                                        : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                                                                    : o === 'Fail'
                                                                    ? active
                                                                        ? 'border-red-600 bg-red-600 text-white'
                                                                        : 'border-red-200 text-red-700 hover:bg-red-50'
                                                                    : active
                                                                    ? 'border-gray-600 bg-gray-700 text-white'
                                                                    : 'border-gray-200 text-gray-700 hover:bg-gray-50';
                                                            return (
                                                                <button
                                                                    key={o}
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setFilterOutcomes(prev => toggleSetValue(prev, o))
                                                                    }
                                                                    className={`inline-flex items-center gap-1 rounded-full border bg-white px-2.5 py-0.5 text-[11px] font-semibold transition-all dark:bg-gray-800 ${tone}`}
                                                                >
                                                                    {o === 'Pass' ? (
                                                                        <CheckCircle className="h-2.5 w-2.5" />
                                                                    ) : o === 'Fail' ? (
                                                                        <XCircle className="h-2.5 w-2.5" />
                                                                    ) : null}
                                                                    {o}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Quick toggles */}
                                            <div>
                                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                    Quick filters
                                                </p>
                                                <div className="flex flex-wrap gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => setFilterDelayOnly(v => !v)}
                                                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-all ${
                                                            filterDelayOnly
                                                                ? 'border-purple-600 bg-purple-600 text-white'
                                                                : 'border-gray-200 bg-white text-gray-700 hover:border-purple-300 hover:bg-purple-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                                                        }`}
                                                    >
                                                        <Timer className="h-2.5 w-2.5" />
                                                        Delayed only
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFilterIssueOnly(v => !v)}
                                                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-all ${
                                                            filterIssueOnly
                                                                ? 'border-amber-600 bg-amber-600 text-white'
                                                                : 'border-gray-200 bg-white text-gray-700 hover:border-amber-300 hover:bg-amber-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                                                        }`}
                                                    >
                                                        <AlertTriangle className="h-2.5 w-2.5" />
                                                        Has issue
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Scrollable Table - p-datatable-sm style */}
                            <div className="relative max-h-[calc(100vh-280px)] min-h-[400px] overflow-auto custom-scrollbar">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 p-datatable-sm">
                                    <thead className="sticky top-0 z-20 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                        <tr>
                                            <th className="sticky left-0 z-10 bg-gray-100 dark:bg-gray-800 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700 min-w-[140px]">Equipment</th>
                                            <th className="sticky left-[140px] z-10 bg-gray-100 dark:bg-gray-800 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700 min-w-[160px]">Checkpoint</th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700 min-w-[140px]">Spec</th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700 whitespace-nowrap">Freq</th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Planned</th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Assigned</th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Start</th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">End</th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Actual</th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Delay</th>
                                            <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Before</th>
                                            <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">After</th>
                                            <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Outcome</th>
                                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700 min-w-[120px]">Remarks</th>
                                            {showIssueColumns && (
                                                <>
                                                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700 min-w-[120px]">Issue Desc</th>
                                                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Priority</th>
                                                    <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Iss Before</th>
                                                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700 min-w-[120px]">Action Taken</th>
                                                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Iss Planned</th>
                                                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Resolution</th>
                                                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Iss Delay</th>
                                                    <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">Iss After</th>
                                                    <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">Status</th>
                                                </>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
                                        {paginatedTasks.length === 0 ? (
                                            <tr>
                                                <td colSpan={tableColumnCount} className="px-4 py-12 text-center">
                                                    <div className="flex flex-col items-center justify-center">
                                                        <AlertCircle className="h-10 w-10 text-gray-400" />
                                                        <p className="mt-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                            {filterCount > 0 ? 'No tasks match the active filters' : 'No tasks found'}
                                                        </p>
                                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                            {filterCount > 0
                                                                ? 'Try clearing one or more filters to see more results.'
                                                                : 'No maintenance tasks for the selected date range.'}
                                                        </p>
                                                        {filterCount > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={clearAllFilters}
                                                                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                                            >
                                                                <XIcon className="h-3 w-3" />
                                                                Clear all filters
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedTasks.map((task, idx) => (
                                                <tr
                                                    key={idx}
                                                    className="group transition-colors hover:bg-blue-50/60 dark:hover:bg-blue-900/10"
                                                >
                                                    <td className="sticky left-0 bg-white dark:bg-gray-800 group-hover:bg-blue-50/80 dark:group-hover:bg-blue-900/20 px-3 py-2 text-xs font-semibold text-gray-900 dark:text-white border-r border-gray-100 dark:border-gray-700 min-w-[140px]">{task.equipmentName}</td>
                                                    <td className="sticky left-[140px] bg-white dark:bg-gray-800 group-hover:bg-blue-50/80 dark:group-hover:bg-blue-900/20 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-gray-700 min-w-[160px]">{task.checkpointName}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 min-w-[140px] max-w-[180px] truncate" title={task.specification}>{task.specification || '-'}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 whitespace-nowrap">{task.frequency || '-'}</td>
                                                    <td className="px-3 py-2 text-xs font-medium text-gray-800 dark:text-gray-200 border-r border-gray-100 dark:border-gray-700 whitespace-nowrap">{task.plannedDate || '-'}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 whitespace-nowrap">{task.assignedTo || '-'}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 whitespace-nowrap">{task.startTime ? task.startTime.split(' ')[1] : '-'}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 whitespace-nowrap">{task.endTime ? task.endTime.split(' ')[1] : '-'}</td>
                                                    <td className="px-3 py-2 text-xs font-medium text-gray-800 dark:text-gray-200 border-r border-gray-100 dark:border-gray-700 whitespace-nowrap">{task.actualDate || '-'}</td>
                                                    <td className={`px-3 py-2 text-xs font-semibold border-r border-gray-100 dark:border-gray-700 ${task.delay > 0 ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400' : 'text-gray-500'}`}>
                                                        {task.delay > 0 ? `${task.delay}d` : '-'}
                                                    </td>
                                                    <td className="px-2 py-2 text-center border-r border-gray-100 dark:border-gray-700">
                                                        <ImageThumbCell
                                                            urls={task.beforeImages}
                                                            title={`${task.equipmentName} - Before`}
                                                            onClick={() => handleViewImages(task.beforeImages || [], `${task.equipmentName} - Before`)}
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2 text-center border-r border-gray-100 dark:border-gray-700">
                                                        <ImageThumbCell
                                                            urls={task.afterImages}
                                                            title={`${task.equipmentName} - After`}
                                                            onClick={() => handleViewImages(task.afterImages || [], `${task.equipmentName} - After`)}
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2 text-center border-r border-gray-100 dark:border-gray-700">
                                                        <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                                            task.outcome === 'Pass'
                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                                                                : task.outcome === 'Fail'
                                                                ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                                                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                                        }`}>
                                                            {task.outcome === 'Pass' ? <CheckCircle className="h-2.5 w-2.5" /> : task.outcome === 'Fail' ? <XCircle className="h-2.5 w-2.5" /> : null}
                                                            {task.outcome}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 min-w-[120px] max-w-[160px] truncate" title={task.remarks || undefined}>{task.remarks || '-'}</td>
                                                    {showIssueColumns && (
                                                        <>
                                                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 min-w-[120px] max-w-[160px] truncate" title={task.issueDescription || undefined}>{task.issueDescription || '-'}</td>
                                                            <td className={`px-3 py-2 text-xs font-semibold border-r border-gray-100 dark:border-gray-700 ${
                                                                task.issuePriority === 'High'
                                                                    ? 'text-red-700 dark:text-red-400'
                                                                    : task.issuePriority === 'Medium'
                                                                    ? 'text-amber-700 dark:text-amber-400'
                                                                    : 'text-green-700 dark:text-green-400'
                                                            }`}>{task.issuePriority || '-'}</td>
                                                            <td className="px-2 py-2 text-center border-r border-gray-100 dark:border-gray-700">
                                                                <ImageThumbCell
                                                                    urls={task.issueBeforeImages}
                                                                    title={`${task.equipmentName} - Issue Before`}
                                                                    onClick={() => handleViewImages(task.issueBeforeImages || [], `${task.equipmentName} - Issue Before`)}
                                                                />
                                                            </td>
                                                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 min-w-[120px] max-w-[160px] truncate" title={task.actionTaken || undefined}>{task.actionTaken || '-'}</td>
                                                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 whitespace-nowrap">{task.issuePlannedDate || '-'}</td>
                                                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 whitespace-nowrap">{task.resolutionDate || '-'}</td>
                                                            <td className={`px-3 py-2 text-xs font-semibold border-r border-gray-100 dark:border-gray-700 ${task.issueDelay > 0 ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400' : 'text-gray-500'}`}>
                                                                {task.issueDelay > 0 ? `${task.issueDelay}d` : '-'}
                                                            </td>
                                                            <td className="px-2 py-2 text-center border-r border-gray-100 dark:border-gray-700">
                                                                <ImageThumbCell
                                                                    urls={task.issueAfterImages}
                                                                    title={`${task.equipmentName} - Issue After`}
                                                                    onClick={() => handleViewImages(task.issueAfterImages || [], `${task.equipmentName} - Issue After`)}
                                                                />
                                                            </td>
                                                            <td className="px-2 py-2 text-center">
                                                                <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                                                    task.status === 'completed'
                                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                                                                        : task.status === 'in_progress'
                                                                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                                                                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                                                }`}>
                                                                    {task.status === 'completed' ? <CheckCircle className="h-2.5 w-2.5" /> : task.status === 'in_progress' ? <Clock className="h-2.5 w-2.5" /> : null}
                                                                    {task.status}
                                                                </span>
                                                            </td>
                                                        </>
                                                    )}
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Compact Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800/80">
                                    <div className="flex items-center gap-3">
                                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                            <span className="font-semibold text-gray-800 dark:text-gray-200">{(previewPage - 1) * previewLimit + 1}</span>–
                                            <span className="font-semibold text-gray-800 dark:text-gray-200">{Math.min(previewPage * previewLimit, filteredTasks.length)}</span> of{' '}
                                            <span className="font-semibold text-blue-600 dark:text-blue-400">{filteredTasks.length}</span>
                                            {filterCount > 0 && (
                                                <span className="ml-1 text-gray-400">
                                                    (filtered from {previewData.tasks?.length || 0})
                                                </span>
                                            )}
                                        </p>
                                        <select
                                            value={previewLimit}
                                            onChange={(e) => {
                                                setPreviewLimit(Number(e.target.value));
                                                setPreviewPage(1);
                                            }}
                                            className="rounded border border-gray-200 bg-white px-2 py-1 text-xs font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setPreviewPage(p => Math.max(1, p - 1))}
                                            disabled={previewPage === 1}
                                            className="inline-flex h-8 items-center gap-1 rounded border border-gray-200 bg-white px-2.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                                        >
                                            <ChevronLeft className="h-3.5 w-3.5" />
                                            Prev
                                        </button>
                                        <span className="rounded border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                            {previewPage} / {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setPreviewPage(p => Math.min(totalPages, p + 1))}
                                            disabled={previewPage === totalPages}
                                            className="inline-flex h-8 items-center gap-1 rounded border border-gray-200 bg-white px-2.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                                        >
                                            Next
                                            <ChevronRight className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>

            <ImageViewerModal
                isOpen={!!viewingImages}
                onClose={() => setViewingImages(null)}
                images={viewingImages?.urls || []}
                title={viewingImages?.title}
            />
        </div>
    );
};

export default PreviewReport;
